/**
 * FundingHistoryPage
 * Displays funding history using the pending funding list and a summary.
 * Route: /funding/history
 */

import { useMemo } from 'react';
import { RefreshCw, AlertTriangle, DollarSign } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { HelpButton } from '@/features/help';
import { useFunding } from '@/features/funding/hooks';
import { FundingHistoryTable } from '@/features/funding/components/FundingHistoryTable';
import { formatCurrency } from '@/utils/formatters';
import { ROUTES } from '@/constants';

// ─── Constants ───────────────────────────────────────────────────────────────

const BREADCRUMBS = [
  { label: 'Inicio', path: ROUTES.DASHBOARD },
  { label: 'Fondeo', path: ROUTES.FUNDING_INSTRUCTIONS },
  { label: 'Historial', path: ROUTES.FUNDING_HISTORY },
];

// ─── Skeleton ────────────────────────────────────────────────────────────────

function PageSkeleton() {
  return (
    <div className="space-y-6">
      <Skeleton variant="rectangular" height={100} />
      <Skeleton variant="rectangular" height={300} />
    </div>
  );
}

// ─── Error State ─────────────────────────────────────────────────────────────

function PageError({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-lg border border-border bg-card px-6 py-16">
      <AlertTriangle size={48} className="mb-4 text-muted-foreground" />
      <h3 className="text-lg font-semibold text-foreground">
        Error al cargar el historial de fondeo
      </h3>
      <p className="mt-1 text-sm text-muted-foreground">
        Hubo un error al obtener los datos. Intenta de nuevo.
      </p>
      <Button className="mt-4" onClick={onRetry}>
        <RefreshCw size={16} />
        Reintentar
      </Button>
    </div>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────────────

export function FundingHistoryPage() {
  const {
    pending,
    isLoadingPending,
    isErrorPending,
    refetchPending,
  } = useFunding();

  const totalAmount = useMemo(
    () => (pending ?? []).reduce((sum, item) => sum + item.amount_mxn, 0),
    [pending],
  );

  if (isLoadingPending) {
    return (
      <div>
        <PageHeader
          title="Historial de Fondeo"
          breadcrumbs={BREADCRUMBS}
        />
        <PageSkeleton />
      </div>
    );
  }

  if (isErrorPending) {
    return (
      <div>
        <PageHeader
          title="Historial de Fondeo"
          breadcrumbs={BREADCRUMBS}
        />
        <PageError onRetry={() => refetchPending()} />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Historial de Fondeo"
        breadcrumbs={BREADCRUMBS}
        actions={
          <div className="flex items-center gap-2">
            <HelpButton pageKey="funding-history" />
            <Button onClick={() => refetchPending()}>
              <RefreshCw size={16} />
              Actualizar
            </Button>
          </div>
        }
      />

      {/* Summary */}
      <div className="mb-6">
        <Card>
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-primary/10 p-2">
              <DollarSign size={20} className="text-primary" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total pendiente</p>
              <p className="text-2xl font-semibold tabular-nums">
                {formatCurrency(totalAmount, 'MXN', 'es-MX')}
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* History Table */}
      <FundingHistoryTable items={pending ?? []} />
    </div>
  );
}
